import { X } from "lucide-react";

import { useT } from "../../i18n";
import { SATELLITE_OPTIONS } from "../../data/satellites";
import { useFormat } from "../../lib/format";
import { useFiltersStore } from "../../store/filtersStore";

interface Chip {
  key: string;
  label: string;
  onRemove: () => void;
}

/**
 * Active applied filters, shown as removable chips under the header (brief §7).
 * They're the answer to "why is there so little on the map" — the biggest
 * source of confusion in a filtered view — so they're always visible when set.
 */
export function ActiveFilterChips() {
  const t = useT();
  const fmt = useFormat();
  const applied = useFiltersStore((s) => s.applied);
  const clearApplied = useFiltersStore((s) => s.clearApplied);
  const removeAppliedSatellites = useFiltersStore((s) => s.removeAppliedSatellites);

  const chips: Chip[] = [];

  if (applied.from || applied.to) {
    const from = applied.from ? fmt.date(applied.from) : "…";
    const to = applied.to ? fmt.date(applied.to) : "…";
    chips.push({
      key: "period",
      label: `${t.filters.chipPeriod}: ${from} — ${to}`,
      onRemove: () => clearApplied("period"),
    });
  }

  // Group satellite chips the same way the panel does: one chip per option
  // (so Landsat 8/9 is a single chip), removing all of the option's satellites.
  for (const opt of SATELLITE_OPTIONS) {
    const present = opt.sats.filter((s) => applied.satellites.includes(s));
    if (present.length) {
      chips.push({
        key: `sat-${opt.key}`,
        label: opt.label,
        onRemove: () => removeAppliedSatellites(opt.sats),
      });
    }
  }

  if (applied.minRate > 0) {
    chips.push({
      key: "minRate",
      label: `${t.filters.chipMinRate}: ≥ ${applied.minRate} ${t.stats.emissions}`,
      onRemove: () => clearApplied("minRate"),
    });
  }

  if (!chips.length) return null;

  return (
    <div data-map-overlay="true" className="flex flex-wrap items-center gap-1.5 rounded-panel border border-border-subtle bg-surface-raised/80 px-2.5 py-1.5 shadow-md backdrop-blur">
      {chips.map((chip) => (
        <span
          key={chip.key}
          className="inline-flex items-center gap-1 rounded-full border border-accent-border bg-accent-subtle py-0.5 pl-2.5 pr-1 text-xs text-text-primary shadow-sm"
        >
          {chip.label}
          <button
            type="button"
            aria-label={`${t.filters.removeFilter}: ${chip.label}`}
            onClick={chip.onRemove}
            className="inline-flex h-4 w-4 items-center justify-center rounded-full text-text-tertiary transition-colors hover:bg-surface-overlay hover:text-text-primary"
          >
            <X size={12} />
          </button>
        </span>
      ))}
    </div>
  );
}
